import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Marks the card as holding internal-only or client-visible content.
   *
   * CR-25 is the leak class where someone believed a panel was internal. When this is
   * set the card renders a persistent text label as well as a colour, because colour
   * alone is not sufficient to distinguish them (screens doc §7.2).
   */
  sensitivity?: 'internal' | 'client-facing';
}

export function Card({ className, sensitivity, children, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border bg-surface shadow-sm',
        sensitivity === 'internal' && 'border-internal/40 ring-1 ring-internal/10',
        sensitivity === 'client-facing' && 'border-client-facing/40 ring-1 ring-client-facing/10',
        className,
      )}
      {...props}
    >
      {sensitivity && (
        <div
          className={cn(
            'flex items-center gap-1.5 rounded-t-lg border-b px-4 py-1.5 text-xs font-semibold uppercase tracking-wide',
            sensitivity === 'internal'
              ? 'border-internal/30 bg-internal-subtle text-internal'
              : 'border-client-facing/30 bg-client-facing-subtle text-client-facing',
          )}
        >
          {sensitivity === 'internal' ? 'Internal only' : 'Visible to the client'}
        </div>
      )}
      {children}
    </div>
  );
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  /** Buttons, menus, filters. Right-aligned. */
  actions?: ReactNode;
}

export function CardHeader({
  title,
  description,
  actions,
  className,
  ...props
}: CardHeaderProps) {
  return (
    <div
      className={cn('flex items-start justify-between gap-4 border-b px-4 py-3', className)}
      {...props}
    >
      <div className="min-w-0">
        <h3 className="truncate text-lg font-semibold text-ink">{title}</h3>
        {description && <p className="mt-0.5 text-sm text-ink-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-4 py-4', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 rounded-b-lg border-t bg-surface-sunken px-4 py-3',
        className,
      )}
      {...props}
    />
  );
}
